import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Tree, Typography } from 'antd';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Segments from './segments';
import { findObjectById } from '../../../../../helpers/utils/functions';
import {
  generateListKeys,
  getParentKey,
  prepareData,
  migrateFiltersWithColor,
} from '../../../../../helpers/utils/reports';
import FilterItemSelected from '../../../../../shared/common/components/reports/params/filter/FilterItemSelected';
import { getStartEndType } from '../../../../../shared/common/components/reports/params/filter/pickers/DateTimeRangePicker';
import RefreshButton from '../../../../../shared/common/components/reports/params/filter/RefreshButton';
import RequestButton from '../../../../../shared/common/components/reports/params/filter/RequestButton';
import {
  data,
  updateFilters,
  calendarTypes,
} from '../../../../../shared/common/constants/filters';
import { useActions } from '../../../../../shared/common/hooks/useActions';
import { useForceUpdate } from '../../../../../shared/common/hooks/useForceUpdate';
import { useLoadGroupingsData } from '../../../../../shared/common/hooks/useLoadGroupingsData';
import { useTypedSelector } from '../../../../../shared/common/hooks/useTypedSelector';
import { LAZY_FILTER_NAMES } from '../../../../../shared/common/components/reports/params/filter/types';

interface IUniversalFilterProps {
  isCompare: boolean;
  withSegment: boolean;
  disabled?: boolean;
  disableAttr?: boolean;
  filtersFilter?: string[];
  withLabel?: boolean;
  notParams?: boolean;
  isModal?: boolean;
  setUpdateDashboard?: () => void;
}

/**
 * Рекурсивно фильтрует дерево, оставляя только узлы,
 * совпадающие с поиском, и их родителей.
 */
const filterTreeBySearch = (nodes: any[], search: string): any[] => {
  if (!search) return nodes;
  const reg = new RegExp(search, 'i');

  return nodes.reduce((acc: any[], node: any) => {
    const nameMatch = reg.test(node.name);

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTreeBySearch(node.children, search);
      if (nameMatch || filteredChildren.length > 0) {
        acc.push({ ...node, children: nameMatch ? node.children : filteredChildren });
      }
    } else {
      if (nameMatch) {
        acc.push(node);
      }
    }
    return acc;
  }, []);
};

// Базовый список ключей поиска; секции разобранных UTM-меток динамические —
// в компоненте используется searchListKeys
export const listKeys = generateListKeys(data);

const UniversalFilter: FC<IUniversalFilterProps> = ({
  isCompare,
  withSegment,
  filtersFilter,
  disabled = false,
  disableAttr = false,
  withLabel = true,
  notParams = false,
  isModal = false,
  setUpdateDashboard,
}) => {
  const forceUpdate = useForceUpdate();
  const locationState = useLocation();
  // Раздельные селекторы — предотвращают лишние ре-рендеры при изменении несвязанных полей
  const filters = useTypedSelector(
    (state) => isModal ? state.filter.dashboardModal.filters : state.filter.filters
  );
  const filterParent = useTypedSelector(
    (state) => isModal ? state.filter.dashboardModal.filterParent : state.filter.filterParent
  );
  const { attribution, type_applications } = useTypedSelector(
    (state) => isModal ? state.attributes.attributes : state.attributes.attributesDashboard
  );
  const [filterVisible, setFilterVisible] = useState(false);
  const { currentDashboard } = useTypedSelector((state) => state.reports);

  const {
    setMetaCompare,
    setFilterParent,
    setFilters,
    setCurrentSegment,
    setAllDataFetching,
    setAllBlocksFetching,
    setCurrentFunnel,
    setMetaModalTableFetching,
    setMetaModalChartFetching,
  } = useActions();
  const isMountRef = useRef(true);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeData, setTreeData] = useState<any>([]);
  const [rawData, setRawData] = useState<any[]>(data);

  const searchListKeys = useMemo(() => generateListKeys(data), []);
  const [btnDisabled, setBtnDisabled] = useState(true);

  // Ленивая загрузка доп. полей (доп. поля пользователя/регистрации/заказа/заявки)
  const { onLoadData } = useLoadGroupingsData(setRawData);

  // Помечает родительские узлы доп. полей как имеющие ленивую загрузку (isLeaf: false),
  // чтобы antd Tree рисовал стрелку и вызывал loadData при клике.
  const applyLazyFlags = useCallback((nodes: any[]): any[] => {
    if (!Array.isArray(nodes)) return nodes;
    return nodes.map((node: any) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isLazyParent = LAZY_FILTER_NAMES.has(node.name);
      return {
        ...node,
        isLeaf: !isLazyParent && !hasChildren,
        children: hasChildren ? applyLazyFlags(node.children) : node.children,
      };
    });
  }, []);
  useEffect(() => {
    if (isMountRef.current) {
      setTimeout(() => isMountRef.current = false, 1000);
      return;
    }
    setBtnDisabled(false);
  }, [filters, attribution, type_applications]);
  // Загрузка фильтров из currentDashboard — только для основной страницы дашборда (НЕ для модальных окон блоков).
  // Модальные окна блоков управляют своими фильтрами независимо через dashboardModal.
  useEffect(() => {
    if (isModal) return; // Фильтры блока устанавливаются в AddBlockModal/EditBlockModal
    let data_ = data;
    if (location.pathname === '/users/ad-reports') {
      data_ = updateFilters();
    }
    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    const _data = prepareData(
      filteredData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [''],
      '',
      filterParent,
      null,
      isModal
    );
    const url = new URL(window.location.href);
    const currentParams = url.searchParams.get('params');
    const paramsObject = currentParams ? JSON.parse(currentParams) : {};
    if (paramsObject?.filters) {
      let filtersFromParams: any[] = [];
      paramsObject.filters.forEach(
        (filt: { id: string; calendar: { type: string } }) => {
          let extraFields = {};
          if (calendarTypes.includes(filt?.calendar?.type)) {
            const { start, end } = getStartEndType({
              calendar: { type: filt?.calendar?.type },
            });
            extraFields = { ...extraFields, start, end };
          }
          const filterFromParam = {
            ...findObjectById(_data, filt.id),
            ...filt,
            ...extraFields,
          };
          if (!(filtersFromParams.some((item: any) => JSON.stringify(item) === JSON.stringify(filterFromParam)))) {
            filtersFromParams.push(filterFromParam);
          }
        }
      );
      const dashboardFilters = migrateFiltersWithColor(currentDashboard?.values?.filters ?? []);
      setFilters([
        ...filtersFromParams,
        ...dashboardFilters,
      ]);
    } else {
      const dashboardFilters = migrateFiltersWithColor(currentDashboard?.values?.filters ?? []);
      setFilters([
        ...dashboardFilters,
      ]);
    }
  }, [currentDashboard]);
  // Вспомогательная функция: получить данные фильтров.
  // Когда isModal=true (модальное окно блока), делаем глубокую копию, чтобы не мутировать
  // общий объект `data` — иначе DashboardFilter и UniversalFilter перезаписывают
  // друг друга (item.title замыкает isModal из последнего вызова prepareData).
  const getFilterData = () => {
    let data_ = rawData;
    if (location.pathname === '/users/ad-reports') {
      data_ = updateFilters();
    }
    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    // Глубокая копия для модального окна, чтобы не мутировать общие объекты
    return isModal ? _.cloneDeep(filteredData) : filteredData;
  };

  useEffect(() => {
    const filteredData = getFilterData();
    const _data = prepareData(
      filteredData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [''],
      '',
      filterParent,
      null,
      isModal
    );

    if (_data.length === 1 && _data[0]?.key) {
      setAutoExpandParent(true);
      setExpandedKeys([_data[0].key]);
    }

    setTreeData(applyLazyFlags(_data));
    forceUpdate();
  }, [
    rawData,
    searchValue,
    filters,
    filters.length,
    isCompare,
    filterParent,
    location.pathname,
    filtersFilter,
  ]);

  useEffect(() => {
    if (!searchValue) {
      setExpandedKeys([]);
      setAutoExpandParent(false);
      const filteredData = getFilterData();
      const _data = prepareData(
        filteredData,
        searchValue,
        filters,
        disableAttr,
        '',
        '',
        '',
        '',
        [''],
        '',
        filterParent,
        null,
        isModal
      );

      setTreeData(applyLazyFlags(_data));
    }

    forceUpdate();
  }, [searchValue]);

  useEffect(() => {
    if (!isCompare) {
      setMetaCompare({ value: false });
    }
  }, [isCompare]);

  const onExpand = (_expandedKeys: any) => {
    setExpandedKeys(_expandedKeys);
    setAutoExpandParent(false);
  };

  const onSearch = (e: any) => {
    const { value } = e.target;
    let expandedKeys = [];
    if (value !== '') {
      const reg = new RegExp(value, 'i');
      expandedKeys = searchListKeys
        .map((item: any) => {
          if (item.name.search(reg) > -1) {
            return getParentKey(item.id, treeData);
          }
          return null;
        })
        .filter((item: any, i: number, self: any) => item && self.indexOf(item) === i);
    }
    setExpandedKeys(expandedKeys);
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  useEffect(() => {
    setExpandedKeys([]);
    setSearchValue('');
  }, [filters]);
  const onClearFilters = () => {
    setCurrentSegment(null);
    setCurrentFunnel(isModal ? { isModal, data: [] } : []);
    setFilters(isModal ? { isModal, data: [] } : []);
    setFilterParent(isModal ? { isModal, data: [] } : []);
    if (isModal) {
      return;
    }
    setAllDataFetching({ value: true });
  };

  const handleUpdateDashboardData = () => {
    if (isModal) {
      setMetaModalTableFetching({ value: true });
      setMetaModalChartFetching({ value: true });
      return;
    }
    // Для дашборда вызываем setUpdateDashboard из пропсов (передается из Dashboards.tsx)
    if (setUpdateDashboard) {
      setUpdateDashboard();
    } else {
      // Fallback для других страниц
      setAllBlocksFetching({ value: true });
      setTimeout(() => {
        setAllBlocksFetching({ value: false });
      }, 2000);
    }
  };

  // Фильтрация дерева при поиске — на уровне рендера
  const displayTreeData = useMemo(() => {
    const base = disableAttr
      ? treeData.filter((item: any) => item.name !== 'Реклама' && item.name !== 'Регистрации')
      : treeData;
    return searchValue ? filterTreeBySearch(base, searchValue) : base;
  }, [ treeData, searchValue, disableAttr ]);

  const isDashboardPage = locationState.pathname === '/dashboards';
  useEffect(() => {
    setFilterVisible(false);
  }, [filters]);

  // Группировка фильтров: обычные + исключения по цветам (как в отчётах)
  const renderedFilters = useMemo(() => {
    if (!filters.length) return null;

    const regularFilters: { item: any; index: number }[] = [];
    const excludesByColor = new Map<string, { item: any; index: number }[]>();

    filters.forEach((item: any, index: number) => {
      if (item.type === 'datetimerange' && !item.start && !item.end) return;
      const isExclude = item.id?.includes('not_');
      const color = item.color || '';
      if (isExclude && color) {
        if (!excludesByColor.has(color)) {
          excludesByColor.set(color, []);
        }
        excludesByColor.get(color)!.push({ item, index });
      } else {
        regularFilters.push({ item, index });
      }
    });

    const result: React.ReactNode[] = [];
    let isFirst = true;

    // Обычные фильтры
    regularFilters.forEach(({ item, index: itemIndex }) => {
      result.push(
        <Fragment key={`regular-${itemIndex}`}>
          {!isFirst && (
            <Typography.Text type="secondary">
              &nbsp;и&nbsp;
            </Typography.Text>
          )}
          <FilterItemSelected
            arrayIndex={itemIndex}
            itemData={item}
            full_name={item.full_name}
            {...item}
            isModal={isModal}
          />
        </Fragment>
      );
      isFirst = false;
    });

    // Группы исключений по цветам — в рамке с "НЕ"
    excludesByColor.forEach((items, color) => {
      result.push(
        <Fragment key={`exclude-${color}`}>
          {!isFirst && (
            <Typography.Text type="secondary">
              &nbsp;и&nbsp;
            </Typography.Text>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
            <b className="filter-exclude-prefix" style={{ marginRight: 4, marginLeft: 4, alignSelf: 'center' }}>
              НЕ&nbsp;
            </b>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                padding: '2px',
                gap: '4px',
                border: '2px solid #ff4d4f',
                borderRadius: '8px',
              }}
            >
              {items.map(({ item, index: itemIndex }, i) => (
                <Fragment key={`${item.id}-${itemIndex}`}>
                  {i !== 0 && (
                    <Typography.Text type="secondary" style={{ margin: '0 4px' }}>
                      &nbsp;и&nbsp;
                    </Typography.Text>
                  )}
                  <FilterItemSelected
                    arrayIndex={itemIndex}
                    itemData={item}
                    full_name={item.full_name}
                    isModal={isModal}
                    {...item}
                  />
                </Fragment>
              ))}
            </span>
          </span>
        </Fragment>
      );
      isFirst = false;
    });

    return result;
  }, [filters, isModal]);

  return (
    <>
      {withSegment ? (
        <Segments disabled={disabled} isModal={isModal} />
      ) : null}
      <RefreshButton />
      {filters.length > 0 && (
        <Button
          style={{ marginRight: 5 }}
          onClick={() => onClearFilters()}
          icon={<CloseOutlined />}
          title="Очистить"
        />
      )}
      <div
        style={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginRight: isDashboardPage ? 24 : 0,
        }}
      >
        {withLabel && (
          <Typography.Text type="secondary">
            Условия фильтра:&nbsp;
          </Typography.Text>
        )}


        {renderedFilters}

        &nbsp;
        <Popover
          content={
            <Tree.DirectoryTree
              blockNode
              switcherIcon={<DownOutlined />}
              showIcon={false}
              selectable={false}
              treeData={displayTreeData}
              loadData={onLoadData}
              onExpand={onExpand}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
            />
          }
          title={
            <Input
              onChange={(e) => onSearch(e)}
              placeholder="Найти условие"
              allowClear
            />
          }
          trigger="click"
          placement="bottom"
          overlayClassName="filter-popover"
          open={filterVisible}
          onOpenChange={setFilterVisible}
        >
          <Button icon={<PlusOutlined />} type="primary" shape="circle" />
        </Popover>
        {(!notParams || isModal) && (
          <RequestButton
            setUpdateDashboard={handleUpdateDashboardData}
            btnDisabled={btnDisabled}
            setBtnDisabled={setBtnDisabled}
          />
        )}
      </div>
    </>
  );
};

export default UniversalFilter;

